import { FormikValues } from "formik";
import {
  Option,
  SecuritiesFormikValues,
  Step,
  StepEndpointSecurities,
  StepThreeSecurities,
} from "../../types";
import styles from "../../styles/securities.module.scss";
import { validationsForLanguage } from "../../validations";
import {
  labels,
  placeholders,
  texts,
  title,
  tooltips,
} from "../../i18n/securities/stepThree";
import { employmentStatus } from "../../i18n/securities/options";
import { holder } from "../../i18n/securities/preconfirmationStep";
import { CreateDraftProps, sendStep } from "../../connectors/connectors";
import { addSendedStep } from "../../reducers/securitiesReducer";
import { formatAsCurrency } from "../../utils";
import InfoIcon from "../../assets/images/icons/ui/tooltip.svg";

// Work and finantial information form

const { formSectionTitle, label } = styles;

const StepThree = ({
  language,
  countriesOptions,
  currentHolder,
}: {
  language: "es" | "en";
  countriesOptions: Option[];
  currentHolder: number;
}): Step => {
  const validations = validationsForLanguage(language);

  const formatIncomeRange = (
    lowerBound: string | number,
    upperBound: string | number
  ) => {
    const formatted = [
      labels[language].fromPrefix,
      formatAsCurrency(lowerBound, language === "en" ? "en-US" : "es-AR", true),
      labels[language].toConnector,
      formatAsCurrency(upperBound, language === "en" ? "en-US" : "es-AR", true),
    ].join(" ");
    return formatted;
  };

  return {
    step: 3,
    name: `${title[language]} ${
      currentHolder !== 1 ? `- (${holder[language]} ${currentHolder})` : ""
    }`,
    titleOfStep: title[language],
    label: title[language],
    fields: [
      {
        label: "",
        initialValue: "",
        type: "label",
        name: "section1",
        text: (
          <>
            <p className={formSectionTitle}>{texts[language].section1}</p>
          </>
        ),
      },
      {
        name: "employmentStatus",
        type: "select",
        label: labels[language].employmentStatus,
        placeholder: placeholders[language].employmentStatus,
        validation: validations.radioOrSingleSelectRequired(
          employmentStatus[language]
        ),
        options: employmentStatus[language],
        selectType: "single",
        initialValue: "",
      },
      {
        name: "occupation",
        label: labels[language].occupation,
        tooltip: {
          label: "",
          content: tooltips[language].occupation,
          icon: InfoIcon,
        },
        type: "text",
        validation: validations.smallStringRequired,
        initialValue: "",
        placeholder: placeholders[language].occupation,
        columns: 2,
        conditional: {
          name: "employmentStatus",
          value: ["EMPLOYEE", "INDEPENDENT"],
        },
      },
      {
        name: "yearsWorked",
        type: "text",
        label: labels[language].yearsWorked,
        placeholder: placeholders[language].yearsWorked,
        validation: validations.numberRequired,
        selectType: "single",
        initialValue: "",
        columns: 2,
        conditional: {
          name: "employmentStatus",
          value: ["EMPLOYEE", "INDEPENDENT"],
        },
      },
      {
        name: "companyType",
        label: labels[language].companyType,
        type: "text",
        validation: validations.smallStringRequired,
        initialValue: "",
        placeholder: placeholders[language].companyType,
        conditional: {
          name: "employmentStatus",
          value: ["EMPLOYEE", "INDEPENDENT", "RETIRED"],
        },
      },
      {
        name: "previousOccupation",
        label: labels[language].previousOccupation,
        type: "text",
        validation: validations.smallStringRequired,
        initialValue: "",
        placeholder: placeholders[language].previousOccupation,
        columns: 2,
        conditional: {
          name: "employmentStatus",
          value: ["UNEMPLOYED", "RETIRED"],
        },
      },
      {
        name: "yearsPreviouslyWorked",
        type: "text",
        label: labels[language].yearsPreviouslyWorked,
        placeholder: placeholders[language].yearsPreviouslyWorked,
        validation: validations.numberRequired,
        selectType: "single",
        initialValue: "",
        columns: 2,
        conditional: {
          name: "employmentStatus",
          value: ["UNEMPLOYED", "RETIRED"],
        },
      },
      {
        label: "",
        initialValue: "",
        type: "label",
        name: "section2",
        text: (
          <>
            <p className={formSectionTitle}>{texts[language].section2}</p>
          </>
        ),
        conditional: {
          name: "employmentStatus",
          value: ["EMPLOYEE", "RETIRED"],
        },
      },
      {
        name: "companyName",
        label: labels[language].companyName,
        type: "text",
        validation: validations.smallStringRequired,
        initialValue: "",
        placeholder: placeholders[language].companyName,
        conditional: {
          name: "employmentStatus",
          value: ["EMPLOYEE", "RETIRED"],
        },
      },
      {
        name: "companyCountry",
        type: "select",
        label: labels[language].companyCountry,
        placeholder: placeholders[language].companyCountry,
        validation: validations.radioOrSingleSelectRequired(countriesOptions),
        options: countriesOptions,
        selectType: "single",
        initialValue: "",
        conditional: {
          name: "employmentStatus",
          value: ["EMPLOYEE", "RETIRED"],
        },
      },
      {
        name: "companyAddresses.fullAddress",
        type: "address",
        label: labels[language].companyAddresses.fullAddress,
        validation: validations.mediumStringRequired,
        initialValue: "",
        placeholder: placeholders[language].companyAddresses.fullAddress,
        onChange: (values, setFieldValue) => {
          const zipCode = values.formattedAddress?.postalCode;
          const state = values.formattedAddress?.state;
          const city = values.formattedAddress?.city;
          const street = values.formattedAddress?.street;
          const number = values.formattedAddress?.streetNumber;

          setFieldValue("companyAddresses.zipCode", zipCode);
          setFieldValue("companyAddresses.state", state);
          setFieldValue("companyAddresses.city", city);
          setFieldValue(
            "companyAddresses.fullAddress",
            `${street} ${number}`,
            true
          );
        },
        autoComplete: "off",
        conditional: {
          name: "employmentStatus",
          value: ["EMPLOYEE", "RETIRED"],
        },
      },
      {
        name: "companyAddresses.zipCode",
        type: "text",
        label: labels[language].companyAddresses.zipCode,
        validation: validations.zipCodeRequired,
        initialValue: "",
        placeholder: placeholders[language].companyAddresses.zipCode,
        conditional: {
          name: "employmentStatus",
          value: ["EMPLOYEE", "RETIRED"],
        },
      },
      {
        name: "companyAddresses.floor",
        type: "text",
        label: labels[language].companyAddresses.floor,
        validation: validations.smallString,
        initialValue: "",
        placeholder: placeholders[language].companyAddresses.floor,
        columns: 2,
        conditional: {
          name: "employmentStatus",
          value: ["EMPLOYEE", "RETIRED"],
        },
      },
      {
        name: "companyAddresses.apartment",
        type: "text",
        label: labels[language].companyAddresses.apartment,
        validation: validations.smallString,
        initialValue: "",
        placeholder: placeholders[language].companyAddresses.apartment,
        columns: 2,
        conditional: {
          name: "employmentStatus",
          value: ["EMPLOYEE", "RETIRED"],
        },
      },
      {
        name: "companyAddresses.city",
        type: "text",
        label: labels[language].companyAddresses.city,
        validation: validations.smallStringRequired,
        initialValue: "",
        columns: 2,
        placeholder: placeholders[language].companyAddresses.city,
        conditional: {
          name: "employmentStatus",
          value: ["EMPLOYEE", "RETIRED"],
        },
      },
      {
        name: "companyAddresses.state",
        type: "text",
        label: labels[language].companyAddresses.state,
        validation: validations.smallStringRequired,
        initialValue: "",
        placeholder: placeholders[language].companyAddresses.state,
        conditional: {
          name: "employmentStatus",
          value: ["EMPLOYEE", "RETIRED"],
        },
        columns: 2,
      },
      {
        label: "",
        initialValue: "",
        type: "label",
        name: "section3",
        text: (
          <>
            <p className={formSectionTitle}>{texts[language].section3}</p>
            <p>{texts[language].personalAssetsSubtitle}</p>
          </>
        ),
        conditional: {
          name: "employmentStatus",
          value: ["EMPLOYEE", "RETIRED"],
        },
      },
      {
        label: "",
        initialValue: "",
        type: "label",
        name: "section2-financeInfo",
        text: (
          <>
            <p className={formSectionTitle}>
              {texts[language]["section2-financeInfo"]}
            </p>
            <p>{texts[language].personalAssetsSubtitle}</p>
          </>
        ),
        conditional: {
          name: "employmentStatus",
          value: ["UNEMPLOYED", "INDEPENDENT", "STUDENT"],
        },
      },
      {
        name: "annualIncomeLabel",
        type: "label",
        label: labels[language].annualIncomeLabel,
        text: (
          <span className={label}>{labels[language].annualIncomeLabel}</span>
        ),
        initialValue: "",
        formatInPreConfirmation: (_, formValues) =>
          formatIncomeRange(
            formValues?.annualIncomeLowerBound ?? "",
            formValues?.annualIncomeUpperBound ?? ""
          ),
      },
      {
        name: "annualIncomeLowerBound",
        type: "money",
        label: labels[language].annualIncomeLowerBound,
        placeholder: placeholders[language].annualIncome,
        columns: 2,
        validation: validations.smallerNumberRequiered({
          than: "annualIncomeUpperBound",
          message: texts[language].lowerBoundTooLargeMessage,
        }),
        initialValue: "",
      },
      {
        name: "annualIncomeUpperBound",
        type: "money",
        label: labels[language].annualIncomeUpperBound,
        placeholder: placeholders[language].annualIncome,
        columns: 2,
        validation: validations.largerNumberRequiered({
          than: "annualIncomeLowerBound",
          message: texts[language].upperBoundTooSmallMessage,
        }),
        initialValue: "",
      },
      {
        name: "netIncomeLabel",
        type: "label",
        label: labels[language].netIncomeLabel,
        text: <span className={label}>{labels[language].netIncomeLabel}</span>,
        initialValue: "",
        formatInPreConfirmation: (_, formValues) =>
          formatIncomeRange(
            formValues?.netIncomeLowerBound ?? "",
            formValues?.netIncomeUpperBound ?? ""
          ),
      },
      {
        name: "netIncomeLowerBound",
        type: "money",
        label: labels[language].netIncomeLowerBound,
        placeholder: placeholders[language].netIncome,
        columns: 2,
        validation: validations.smallerNumberRequiered({
          than: "netIncomeUpperBound",
          message: texts[language].lowerBoundTooLargeMessage,
        }),
        initialValue: "",
      },
      {
        name: "netIncomeUpperBound",
        type: "money",
        label: labels[language].netIncomeUpperBound,
        placeholder: placeholders[language].netIncome,
        columns: 2,
        validation: validations.largerNumberRequiered({
          than: "netIncomeLowerBound",
          message: texts[language].upperBoundTooSmallMessage,
        }),
        initialValue: "",
      },
      {
        name: "netWorthLabel",
        type: "label",
        label: labels[language].netWorthLabel,
        text: <span className={label}>{labels[language].netWorthLabel}</span>,
        initialValue: "",
        formatInPreConfirmation: (_, formValues) =>
          formatIncomeRange(
            formValues?.netWorthLowerBound ?? "",
            formValues?.netWorthUpperBound ?? ""
          ),
      },
      {
        name: "netWorthLowerBound",
        type: "money",
        label: labels[language].netWorthLowerBound,
        placeholder: placeholders[language].netWorth,
        columns: 2,
        validation: validations.smallerNumberRequiered({
          than: "netWorthUpperBound",
          message: texts[language].lowerBoundTooLargeMessage,
        }),
        initialValue: "",
      },
      {
        name: "netWorthUpperBound",
        type: "money",
        label: labels[language].netWorthUpperBound,
        placeholder: placeholders[language].netWorth,
        columns: 2,
        validation: validations
          .largerNumberRequiered({
            than: "netWorthLowerBound",
            message: texts[language].upperBoundTooSmallMessage,
          })
          .concat(
            validations.smallerNumberRequiered({
              than: "netIncomeUpperBound",
              orEqual: true,
              message: texts[language].netWorthTooLargeMessage,
            })
          ),
        initialValue: "",
      },
    ],
    onContinue: async (values: FormikValues, dispatch, state) => {
      try {
        if (!state || !state.validations || !dispatch)
          throw new Error("Error: state or dispatch not found");

        const {
          employmentStatus,
          occupation,
          yearsWorked,
          companyType,
          previousOccupation,
          yearsPreviouslyWorked,
          companyName,
          companyCountry,
          companyAddresses,
          annualIncomeUpperBound,
          annualIncomeLowerBound,
          netIncomeLowerBound,
          netIncomeUpperBound,
          netWorthUpperBound,
          netWorthLowerBound,
        } = values as SecuritiesFormikValues["stepThree"];

        const {
          fullAddress,
          city,
          zipCode,
          state: stateAddress,
          floor,
          apartment,
        } = companyAddresses || {};

        const stepOneFormValues = values as SecuritiesFormikValues["stepOne"];

        const { initialValidationData, sendedSteps } = state.securities;

        const sendCompanyInformation = ["EMPLOYEE", "RETIRED"].includes(
          employmentStatus
        );

        if (currentHolder === 1) {
          const step: StepThreeSecurities = {
            "@type": "3_SECURITY",
            workInformation: {
              workSituation: employmentStatus,
              currentOccupation: occupation,
              yearsInCurrentOccupation: Number.parseInt(yearsWorked || "0"),
              currentCompanyType: companyType,
              previousOccupation: previousOccupation,
              yearsInPreviousOccupation: Number.parseInt(
                yearsPreviouslyWorked || "0"
              ),
              ...(sendCompanyInformation
                ? {
                    employerInformation: {
                      name: companyName || "",
                      address: {
                        address: fullAddress || "",
                        postalCode: zipCode || "",
                        city: city || "",
                        province: stateAddress || "",
                        country: companyCountry || "",
                        ...(floor ? { floor: floor } : {}),
                        ...(apartment ? { apartment: apartment } : {}),
                      },
                    },
                  }
                : {}),
              ...(values.employmentStatus === "INDEPENDENT"
                ? {
                    employerInformation: {
                      name: "SELF",
                      address: {
                        address: stepOneFormValues.taxAddresses.fullAddress,
                        postalCode: stepOneFormValues.taxAddresses.zipCode,
                        city: stepOneFormValues.taxAddresses.city,
                        province: stepOneFormValues.taxAddresses.state,
                        country: stepOneFormValues.taxAddresses.country,
                        ...(floor
                          ? { floor: stepOneFormValues.taxAddresses.floor }
                          : {}),
                        ...(apartment
                          ? {
                              apartment:
                                stepOneFormValues.taxAddresses.apartment,
                            }
                          : {}),
                      },
                    },
                  }
                : {}),
            },
            financialInformation: {
              annualIncomeFrom: Number.parseFloat(annualIncomeLowerBound),
              annualIncomeTo: Number.parseFloat(annualIncomeUpperBound),
              netIncomeFrom: Number.parseFloat(netIncomeLowerBound),
              netIncomeTo: Number.parseFloat(netIncomeUpperBound),
              liquidAssetsFrom: Number.parseFloat(netWorthLowerBound),
              liquidAssetsTo: Number.parseFloat(netWorthUpperBound),
            },
          };

          // Previous sent steps
          const previousSteps = sendedSteps.filter(
            (step: StepEndpointSecurities) => step["@type"] !== "3_SECURITY"
          );

          const body: CreateDraftProps["body"] = {
            companyType: "ALLARIA_SECURITY",
            personType: "PHYSICAL",
            email: initialValidationData.email,
            steps: [...previousSteps, step],
          };

          await sendStep({ stepId: 3, body });
          dispatch(addSendedStep(step));
        }
      } catch (error) {
        console.error("Error step three: ", error);
        throw new Error("Error al enviar los datos en el paso 3");
      }
    },
  };
};

export default StepThree;
